/**
 * Utilidades de tiempo y fechas
 */

import {
  format,
  formatDistanceToNow,
  isToday,
  isYesterday,
  isTomorrow,
  startOfDay,
  endOfDay,
  differenceInDays,
  differenceInMinutes,
  addDays,
  parseISO,
} from 'date-fns';
import { es } from 'date-fns/locale';

/**
 * Formatea una fecha en español
 */
export function formatDate(date: Date | string, formatStr = 'PPP'): string {
  const d = typeof date === 'string' ? parseISO(date) : date;
  return format(d, formatStr, { locale: es });
}

/**
 * Formatea tiempo relativo ("hace 2 horas")
 */
export function formatRelative(date: Date | string): string {
  const d = typeof date === 'string' ? parseISO(date) : date;
  return formatDistanceToNow(d, { addSuffix: true, locale: es });
}

/**
 * Obtiene un label amigable para la fecha
 */
export function getDateLabel(date: Date | string): string {
  const d = typeof date === 'string' ? parseISO(date) : date;

  if (isToday(d)) return 'Hoy';
  if (isYesterday(d)) return 'Ayer';
  if (isTomorrow(d)) return 'Mañana';

  return formatDate(d, 'EEEE d');
}

/**
 * Formatea minutos en formato legible
 */
export function formatMinutes(minutes: number): string {
  if (minutes < 60) {
    return `${minutes} min`;
  }

  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;

  if (mins === 0) {
    return `${hours}h`;
  }

  return `${hours}h ${mins}min`;
}

/**
 * Obtiene la fecha actual en formato ISO (solo fecha)
 */
export function getTodayISO(): string {
  return format(new Date(), 'yyyy-MM-dd');
}

/**
 * Obtiene el inicio del día
 */
export function getStartOfDay(date: Date = new Date()): Date {
  return startOfDay(date);
}

/**
 * Obtiene el fin del día
 */
export function getEndOfDay(date: Date = new Date()): Date {
  return endOfDay(date);
}

/**
 * Calcula la diferencia en días entre dos fechas
 */
export function getDaysDifference(date1: Date | string, date2: Date | string): number {
  const d1 = typeof date1 === 'string' ? parseISO(date1) : date1;
  const d2 = typeof date2 === 'string' ? parseISO(date2) : date2;
  return differenceInDays(d1, d2);
}

/**
 * Calcula la diferencia en minutos entre dos fechas
 */
export function getMinutesDifference(date1: Date | string, date2: Date | string): number {
  const d1 = typeof date1 === 'string' ? parseISO(date1) : date1;
  const d2 = typeof date2 === 'string' ? parseISO(date2) : date2;
  return differenceInMinutes(d1, d2);
}

/**
 * Añade días a una fecha
 */
export function addDaysToDate(date: Date, days: number): Date {
  return addDays(date, days);
}

/**
 * Verifica si una fecha es hoy
 */
export function checkIsToday(date: Date | string): boolean {
  const d = typeof date === 'string' ? parseISO(date) : date;
  return isToday(d);
}

/**
 * Obtiene el timestamp actual en ISO
 */
export function getNowISO(): string {
  return new Date().toISOString();
}

/**
 * Formatea hora (HH:mm)
 */
export function formatTime(date: Date | string): string {
  const d = typeof date === 'string' ? parseISO(date) : date;
  return format(d, 'HH:mm');
}
